





























































































































































import _ from 'lodash'
import Component, {mixins} from 'vue-class-component'
import autobahn, {Connection, Registration} from 'autobahn'
import {VDataTableHeader} from '@/types'
import {DateMixin} from '@/modules/core/date'
import ConnectedInstanceDetail from '@/modules/app-router/components/ConnectedInstanceDetail.vue';
import ConnectedUnigestInstance from '@/modules/app-router/model/ConnectedUnigestInstance';
import Wamp from '@/modules/core/wamp/mixins/Wamp';
import {Watch} from 'vue-property-decorator';

enum WampTopic {
    NotifyPresence = 'net.unigest.notify_presence',
    PresenceNotified = 'net.unigest.presence_notified',
}

/**
 * Control de presencia
 *
 * Muestra al usuario una pantalla que permite comprobar las instancias conectadas
 * a UnigestWeb
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
@Component({
    components: {ConnectedInstanceDetail},
})
export default class PresenceControl extends mixins(DateMixin, Wamp) {
    /**
     * Observando
     *
     * Indica si el componente se encuentra escuchando notificaciones de presencia
     * procedentes del enrutador WAMP. Este propiedad es activada cuando
     * el usuario hace click sobre el botón observar y se establece
     * una conexión con el enrutador WAMP para recibir
     * notificaciones.
     *
     * @var { boolean }
     */
    public watching = false

    public sub: autobahn.Subscription | null = null
    public registration: Registration | null = null
    public search = ''
    public displayOnlyConnected = false
    public displayGrid = true

    /**
     * Cabeceras de la tabla
     */
    public headers: VDataTableHeader[] = [
        { value: 'status', text: 'Estado', width: '150px'},
        { value: 'appVersion', text: 'Versión', width: '100px'},
        { value: 'user.id', text: 'Id usuario', width: '120px'},
        { value: 'user.name', text: 'Nombre usuario', width: '150px'},
        { value: 'currentPath', text: 'Ruta'},
        { value: 'apiURL', text: 'API', width: '230px'},
        { value: 'emittedAt', text: 'Última actualización', width: '180px', class: 'text-truncate'},
    ]

    public connectedInstances: ConnectedUnigestInstance[] = []
    public instance = new ConnectedUnigestInstance()
    public instanceDetail = false

    public created () {
        this.displayGrid = JSON.parse(
            localStorage.getItem('presenceControl.displayGrid') || 'false',
        )
    }

    @Watch('displayGrid')
    public watchDisplayGrid (value: boolean) {
        localStorage.setItem('presenceControl.displayGrid', JSON.stringify(value))
    }

    public instanceFilter (items: ConnectedUnigestInstance[], search: string) {
        const displayOnlyConnected = this.displayOnlyConnected

        const valid = (i: ConnectedUnigestInstance) => !!i.user.id
        const includes = (i: ConnectedUnigestInstance) => search ? i.includes(search) : true
        const onlyConnected = (i: ConnectedUnigestInstance) => displayOnlyConnected ? i.status === 'CONNECTED' : true

        return items.filter(item => valid(item) && includes(item) && onlyConnected(item))
    }

    /**
     * Permite obtener un conteo de las instancias que se ha desconectado
     * mientas el control de presencia observaba su actividad
     *
     * @var { number }
     */
    public get disconnectedInstancesCount () {
        return this.connectedInstances
            .filter(i => i.status === 'DISCONNECTED')
            .length
    }

    /**
     * Permite obtener un conteo de las instancias que se encuentran inactivas.
     * Por ejemplo porque el usuario ha salido del navegador sin cerrarlo
     * o porque ha cambiado de pestaña del navegador
     *
     * @var { number }
     */
    public get inactiveInstancesCount () {
        return this.connectedInstances
            .filter(i => i.status === 'INACTIVE')
            .length
    }

    public get filteredInstances (): ConnectedUnigestInstance[] {
        return this.connectedInstances.filter(i => {
            if (!i.user.id || !i.user.name) {
                return false
            }

            return i.status === 'CONNECTED' ? true : !this.displayOnlyConnected
        })
    }

    /**
     * Añade una nueva instancia a la lista de instancias conectadas.
     */
    public addInstance (instance: ConnectedUnigestInstance) {
        const index = this.connectedInstances.findIndex(i => i.id === instance.id)
        index === -1
            ? this.connectedInstances.push(instance)
            : this.connectedInstances.splice(index, 1, instance)
    }

    public statusText = {
        CONNECTED: 'Conectado',
        DISCONNECTED: 'Desconectado',
        INACTIVE: 'Inactivo',
        LOGOUT: 'Sesión cerrada',
    }

    public statusConfig = {
        CONNECTED: {text: 'Conectado', icon: 'mdi-power-plug'},
        DISCONNECTED: {text: 'Desconectado', icon: 'mdi-power-plug-off'},
        INACTIVE: {text: 'Inactivo', icon: 'mdi-sleep'},
        LOGOUT: {text: 'Sesión cerrada', icon: 'mdi-exit-run'},
    }

    public displayInstance (instance: ConnectedUnigestInstance) {
        this.instance = instance
        this.instanceDetail = true
    }

    /**
     * Iniciar observación de notificaciones de presencia
     *
     * Método invocado cuando el usuario pulsa sobre el botón observar, inicia una
     * conexión con el servidor WAMP y solicita a todos las instancias
     * conectadas su presencia en la red.
     */
    public async startWatchPresenceNotification () {
        if (this.watching) {
            return
        }

        this.connectedInstances = []
        this.watching = true
        const session = await this.$wamp.connect()
        // Suscripción al evento lanzado por las instancia de UnigestWeb para notificar
        // que se encuentran activas
        this.sub = await session.subscribe(WampTopic.PresenceNotified, (args: any[], b, c) => {
            let instance: ConnectedUnigestInstance = args[0] || {}
            if (!_.isObject(instance)) {
                throw new Error('El primer argumento del evento debe ser la información de la instancia')
            }

            instance = Object.assign(new ConnectedUnigestInstance(), instance)
            this.addInstance(instance)
            if (instance.id === this.instance.id) {
                this.instance = instance
            }
        })

        await session.subscribe('wamp.session.on_leave', (args: any[]) => {
            const id = args[0] || 0
            this.log('INFO', `La sesión ${id} ha abandonado el enrutador`)

            const session = _.find(this.connectedInstances, { wampSession: id })
            if (!session) {
                return
            }

            session.status = 'DISCONNECTED'
        })

        this.log('INFO', `Añadida suscripción al evento "${this.sub.topic}"`)

        try {
            this.registration = await session.register('net.unigest.check_presence_control_enabled', async () => {
                return this.watching;
            });
        } catch (e) {
            this.log('WARN', e.message)
        }

        session.publish(WampTopic.NotifyPresence)
    }

    /**
     * Detiene la observación de notificaciones de presencia
     */
    public async stopWatchPresenceNotification () {
        if (!this.watching) {
            return
        }

        // Elimina la subscripción al evento utilizando por los clientes para notificar
        // al enrutador que se encuentran conectados.
        this.sub && await this.sub.unsubscribe()
        this.registration && await this.registration.unregister()
        this.log('INFO', `Eliminada suscripción al evento "${this.sub!.topic}"`)

        this.sub = null
        this.watching = false
    }
}
